import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Field, Input } from '@/ui/Field';
import { useGuardarIcono, useSubirPortada, type CursoDetalle } from '../api';
import { IconoDePortada } from '../piezas';

/**
 * Portada — una imagen, o un icono sobre un color.
 *
 * Los doce iconos son un grupo de opciones con flechas y una sola parada de Tab,
 * como la paleta del alta de curso. En el vanilla eran doce `<button>` con
 * `role="radio"` y sin flechas: el grupo anunciaba un comportamiento que no
 * tenía.
 */

const ICONOS: ReadonlyArray<readonly [string, string]> = [
  ['bi-journal-bookmark', 'Libros'], ['bi-mortarboard', 'Birrete'], ['bi-lightbulb', 'Bombilla'],
  ['bi-tools', 'Herramientas'], ['bi-lightning-charge', 'Rayo'], ['bi-trophy', 'Trofeo'],
  ['bi-shield-check', 'Escudo'], ['bi-bullseye', 'Diana'], ['bi-bar-chart-line', 'Gráfica'],
  ['bi-star', 'Estrella'], ['bi-rocket-takeoff', 'Cohete'], ['bi-briefcase', 'Maletín'],
];

/** El límite que aplica el servidor a la portada. */
const MAX_PORTADA_MB = 10;

export function Portada({ curso }: { curso: CursoDetalle }) {
  const subir = useSubirPortada();
  const guardarIcono = useGuardarIcono();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [icono, setIcono] = useState(curso.cover_icon || ICONOS[0]![0]);
  const [color, setColor] = useState(curso.cover_icon_color || '#0D2B4E');
  const botones = useRef<Array<HTMLButtonElement | null>>([]);

  // Mover la elección Y el foco. Con solo la elección, el anillo se quedaba en
  // el icono anterior —que ya tenía `tabIndex` -1— y el lector no anunciaba
  // nada: la flecha parecía no hacer nada.
  function mover(delta: number) {
    const i = Math.max(0, ICONOS.findIndex(([c]) => c === icono));
    const n = (i + delta + ICONOS.length) % ICONOS.length;
    setIcono(ICONOS[n]![0]);
    botones.current[n]?.focus();
  }

  function elegirArchivo(entrada: HTMLInputElement) {
    const f = entrada.files?.[0];
    if (!f) { setArchivo(null); return; }
    if (f.size > MAX_PORTADA_MB * 1024 * 1024) {
      Utils.toast(`«${f.name}» pesa más de ${MAX_PORTADA_MB} MB`, 'warning');
      // Fuera también la anterior: el campo enseñaría la rechazada y se
      // subiría la de antes.
      setArchivo(null);
      entrada.value = '';
      return;
    }
    setArchivo(f);
  }

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="portada-imagen" className="flex flex-col gap-3">
        <h3 id="portada-imagen" className="text-3 font-bold text-brand-ink">Una imagen</h3>
        <Field label="Imagen de portada" ayuda={`JPG o PNG, hasta ${MAX_PORTADA_MB} MB. Sustituye al icono.`}>
          <Input type="file" accept="image/*" onChange={(e) => elegirArchivo(e.currentTarget)} />
        </Field>
        <div>
          <Button
            disabled={!archivo}
            loading={subir.isPending}
            onClick={() =>
              archivo &&
              subir.mutate(
                { cursoId: curso.id, archivo },
                {
                  onSuccess: () => { setArchivo(null); Utils.toast('Portada actualizada', 'success'); },
                  onError: (err) =>
                    Utils.toast(err instanceof Error ? err.message : 'No se pudo subir la imagen', 'error'),
                },
              )
            }
          >
            <i className="bi bi-cloud-upload" aria-hidden="true" /> Subir imagen
          </Button>
        </div>
      </section>

      <section aria-labelledby="portada-icono" className="flex flex-col gap-3 border-t border-line pt-5">
        <h3 id="portada-icono" className="text-3 font-bold text-brand-ink">O un icono sobre un color</h3>
        <div
          role="radiogroup"
          aria-labelledby="portada-icono"
          className="flex flex-wrap gap-2"
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); mover(1); }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); mover(-1); }
          }}
        >
          {ICONOS.map(([c, nombre], idx) => {
            const puesto = c === icono;
            // Si el icono del curso no es ninguno de estos doce —uno elegido con
            // el selector general, o un emoji antiguo—, ninguno está marcado y
            // sin esto ninguno tendría `tabIndex` 0: el grupo entero quedaría
            // fuera del alcance del teclado. Entonces se entra por el primero.
            const entrada = puesto || (idx === 0 && !ICONOS.some(([k]) => k === icono));
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={puesto}
                aria-label={nombre}
                title={nombre}
                tabIndex={entrada ? 0 : -1}
                onClick={() => setIcono(c)}
                ref={(el) => { botones.current[idx] = el; }}
                className={cn(
                  'flex size-12 items-center justify-center rounded-sm border text-7 focus-visible:focus-anillo',
                  puesto ? 'border-brand-ink bg-brand/[.08] text-brand-ink' : 'border-line bg-surface text-ink-2 hover:border-brand-ink',
                )}
              >
                <i className={cn('bi', c)} aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <Field label="Color de fondo">
            <input
              type="color"
              aria-label="Color de fondo del icono"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-11 w-16 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
            />
          </Field>
          <span
            className="flex size-16 items-center justify-center rounded-sm"
            style={{ background: color }}
            aria-hidden="true"
          >
            <IconoDePortada valor={icono} respaldo="bi-journal-bookmark" className="text-8 leading-none text-white" />
          </span>
          <Button
            variant="secondary"
            loading={guardarIcono.isPending}
            onClick={() =>
              guardarIcono.mutate(
                { cursoId: curso.id, cover_icon: icono, cover_icon_color: color },
                {
                  onSuccess: () => Utils.toast('Icono guardado', 'success'),
                  onError: (err) =>
                    Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar el icono', 'error'),
                },
              )
            }
          >
            <i className="bi bi-check-lg" aria-hidden="true" /> Usar este icono
          </Button>
        </div>
      </section>
    </div>
  );
}
